import { Box, Portal, Tooltip } from '@chakra-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FC, useEffect, useState } from 'react';
import { useSelectedLinkNode } from '../hooks';

export const LinkTooltipPlugin: FC = () => {
  const [editor] = useLexicalComposerContext();
  const activeLinkNode = useSelectedLinkNode();
  const [isTooltipOpen, setIsTooltipOpen] = useState(true);

  // the tooltip component requires a child element for placement
  // we get the current selection's DOM node and render an invisible box placed at the DOM node's position
  const [linkRect, setLinkRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!activeLinkNode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- keep everything in one place
      setLinkRect(null);
      return;
    }

    const linkDOM = editor.getElementByKey(activeLinkNode.getKey());
    if (!linkDOM) {
      return;
    }

    // update when scrolling or resizing
    const updateRect = () => {
      setLinkRect(linkDOM.getBoundingClientRect());
    };

    updateRect();

    // use an intersection observer to hide the tooltip when the element is scrolled out of view
    const observer = new IntersectionObserver(([entry]) => setIsTooltipOpen(entry.isIntersecting), { threshold: 0 });

    observer.observe(linkDOM);
    window.addEventListener('scroll', updateRect, true); // passing true to capture nested scrolls
    window.addEventListener('resize', updateRect);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [activeLinkNode, editor]);

  // get URL and text of the current node
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    if (!activeLinkNode) {
      return;
    }

    const removeUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setUrl(activeLinkNode.getURL());
        setText(activeLinkNode.getTextContent());
      });
    });

    return () => {
      removeUpdateListener();
    };
  }, [activeLinkNode, editor]);

  // only render tooltip if we have a link selection and the link's text is different from the URL
  if (!activeLinkNode || !linkRect || url === text) {
    return null;
  }

  return (
    <Portal>
      <Tooltip
        label={url}
        isOpen={isTooltipOpen}
        hasArrow
        placement="bottom"
        sx={{ '[data-popper-placement]:has(>&)': { zIndex: 'calc(var(--chakra-zIndices-popover) - 1)' } }}
      >
        <Box
          position="fixed"
          top={`${linkRect.top}px`}
          left={`${linkRect.left}px`}
          width={`${linkRect.width}px`}
          height={`${linkRect.height}px`}
          pointerEvents="none"
        />
      </Tooltip>
    </Portal>
  );
};
